/**
 * Event detail view — shows event info and management tabs for participants,
 * teams, admins, permissions, and settings. Includes lifecycle controls and
 * close-scoring review.
 *
 * Requirements: 2.2, 2.3, 3.1, 3.5, 3.6, 3.8, 3.9, 4.1, 4.3, 9.1, 11.7, 12.1, 12.2, 12.3
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout, DonationButton, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import { useApiClient } from '../hooks/useApiClient.js';
import { ParticipantManagement } from '../components/ParticipantManagement.js';
import { TeamManagement } from '../components/TeamManagement.js';
import { AdminManagement } from '../components/AdminManagement.js';
import { PermissionManagement } from '../components/PermissionManagement.js';
import { EventLifecycleControls } from '../components/EventLifecycleControls.js';
import { CloseScoreReview } from '../components/CloseScoreReview.js';
import { LeaderboardSettings } from '../components/LeaderboardSettings.js';
import { ConflictResolution } from '../components/ConflictResolution.js';
import type { EventStatus } from '../components/EventLifecycleControls.js';
import type { LeaderboardVisibility } from '../components/LeaderboardSettings.js';
import type { CSSProperties } from 'react';

type Tab = 'participants' | 'teams' | 'admins' | 'permissions' | 'conflicts' | 'settings';

interface EventDetail {
  id: string;
  name: string;
  date: string;
  location: string;
  status: EventStatus;
  leaderboardVisibility: LeaderboardVisibility;
}

const tabListStyle: CSSProperties = {
  display: 'flex',
  gap: '0',
  borderBottom: '1px solid var(--color-border, #E5E7EB)',
  marginBottom: '1.5rem',
};

const tabStyle: CSSProperties = {
  padding: '0.5rem 1rem',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  borderBottom: '2px solid transparent',
  color: 'var(--color-text-secondary, #6B7280)',
};

const activeTabStyle: CSSProperties = {
  ...tabStyle,
  color: 'var(--color-brand-green, #1B4332)',
  borderBottomColor: 'var(--color-brand-green, #1B4332)',
};

const eventInfoStyle: CSSProperties = {
  display: 'flex',
  gap: '1.5rem',
  flexWrap: 'wrap',
  marginBottom: '1.5rem',
  padding: '1rem',
  background: 'var(--color-bg-page, #FAFBFC)',
  borderRadius: 'var(--radius-button, 8px)',
};

const infoItemStyle: CSSProperties = {
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
};

const TABS: { key: Tab; label: string }[] = [
  { key: 'participants', label: 'Participants' },
  { key: 'teams', label: 'Teams' },
  { key: 'admins', label: 'Admins' },
  { key: 'permissions', label: 'Permissions' },
  { key: 'conflicts', label: 'Conflicts' },
  { key: 'settings', label: 'Settings' },
];

export function EventDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const apiClient = useApiClient();
  const [activeTab, setActiveTab] = useState<Tab>('participants');
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCloseReview, setShowCloseReview] = useState(false);

  const fetchEvent = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await apiClient.request<{ event: EventDetail }>('GET', `/events/${encodeURIComponent(id)}`);
      setEvent(data.event);
      setError(null);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load event.');
    } finally {
      setLoading(false);
    }
  }, [id, apiClient]);

  useEffect(() => {
    void fetchEvent();
  }, [fetchEvent]);

  function handleStatusChange(newStatus: EventStatus): void {
    setEvent((prev) => (prev ? { ...prev, status: newStatus } : prev));
  }

  function handleVisibilityChange(visibility: LeaderboardVisibility): void {
    setEvent((prev) => (prev ? { ...prev, leaderboardVisibility: visibility } : prev));
  }

  function handleCloseReviewConfirm(): void {
    setShowCloseReview(false);
    handleStatusChange('complete');
  }

  if (!id) {
    return (
      <AppLayout title="Event Detail" footer={<DonationButton />}>
        <ErrorBanner>No event ID provided.</ErrorBanner>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={event?.name ?? 'Event Detail'} footer={<DonationButton />}>
      <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
        <Link to="/events" style={{ display: 'inline-block', marginBottom: '1rem', color: 'var(--color-brand-green, #1B4332)', textDecoration: 'none' }}>
          ← Back to Events
        </Link>

        {error && (
          <ErrorBanner title="Error" onDismiss={() => setError(null)}>
            {error}
          </ErrorBanner>
        )}

        {loading ? (
          <Spinner label="Loading event…" />
        ) : event ? (
          <>
            {/* Lifecycle controls — status badge + transition buttons */}
            <EventLifecycleControls
              eventId={id}
              status={event.status}
              apiClient={apiClient}
              onStatusChange={handleStatusChange}
              onInitiateClose={() => setShowCloseReview(true)}
            />

            {/* Event info summary */}
            <div style={eventInfoStyle}>
              <div style={infoItemStyle}>
                <strong>Date:</strong> {event.date}
              </div>
              <div style={infoItemStyle}>
                <strong>Location:</strong> {event.location}
              </div>
            </div>

            {/* Tab navigation */}
            <nav aria-label="Event management tabs">
              <div role="tablist" style={tabListStyle}>
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    role="tab"
                    type="button"
                    aria-selected={activeTab === tab.key}
                    aria-controls={`tabpanel-${tab.key}`}
                    id={`tab-${tab.key}`}
                    onClick={() => setActiveTab(tab.key)}
                    style={activeTab === tab.key ? activeTabStyle : tabStyle}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </nav>

            {/* Tab panels */}
            <div
              role="tabpanel"
              id={`tabpanel-${activeTab}`}
              aria-labelledby={`tab-${activeTab}`}
            >
              {activeTab === 'participants' && (
                <ParticipantManagement eventId={id} apiClient={apiClient} />
              )}
              {activeTab === 'teams' && (
                <TeamManagement eventId={id} apiClient={apiClient} />
              )}
              {activeTab === 'admins' && (
                <AdminManagement eventId={id} apiClient={apiClient} />
              )}
              {activeTab === 'permissions' && (
                <PermissionManagement eventId={id} apiClient={apiClient} />
              )}
              {activeTab === 'conflicts' && (
                <ConflictResolution eventId={id} apiClient={apiClient} />
              )}
              {activeTab === 'settings' && (
                <LeaderboardSettings
                  eventId={id}
                  apiClient={apiClient}
                  currentVisibility={event.leaderboardVisibility}
                  onVisibilityChange={handleVisibilityChange}
                />
              )}
            </div>

            {/* Close-scoring review modal */}
            {showCloseReview && (
              <CloseScoreReview
                eventId={id}
                apiClient={apiClient}
                onCancel={() => setShowCloseReview(false)}
                onConfirm={handleCloseReviewConfirm}
              />
            )}
          </>
        ) : null}
      </div>
    </AppLayout>
  );
}
